import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import {
  createMonitoringDashboard,
  deleteMonitoringDashboard,
  listMonitoringDashboards,
  syncMonitoringDashboard,
  type MonitoringDashboard,
} from "@/api/endpoints/adminMonitoring";
import { getTenants } from "@/api/endpoints/tenants";
import type { Tenant } from "@/types";

export default function MonitoringDashboards() {
  const qc = useQueryClient();

  const dashboardsQ = useQuery({
    queryKey: ["admin", "monitoring", "dashboards"],
    queryFn: listMonitoringDashboards,
    refetchInterval: 30_000,
  });
  const tenantsQ = useQuery({ queryKey: ["tenants"], queryFn: getTenants });

  const [createOpen, setCreateOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: createMonitoringDashboard,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "monitoring", "dashboards"] });
      setCreateOpen(false);
    },
  });
  const syncMut = useMutation({
    mutationFn: syncMonitoringDashboard,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "monitoring", "dashboards"] }),
  });
  const deleteMut = useMutation({
    mutationFn: deleteMonitoringDashboard,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "monitoring", "dashboards"] }),
  });

  if (dashboardsQ.error) {
    const msg = (dashboardsQ.error as Error).message;
    return (
      <div className="rounded-lg border border-red-700/40 bg-red-700/10 p-4 text-red-300">
        Failed to load dashboards: {msg}
        {msg.includes("403") && (
          <p className="mt-1 text-sm text-red-200">Admin only.</p>
        )}
      </div>
    );
  }

  const dashboards = dashboardsQ.data ?? [];
  const tenants = tenantsQ.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">
            Tenant Grafana dashboards
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Live admin: each row pushes a per-tenant dashboard to Grafana via
            its HTTP API. Replaces the Ansible <code>monitoring_tenants</code>
            inventory variable for stacks where ops want to add or remove a
            tenant on the fly.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => dashboardsQ.refetch()}
            className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            disabled={tenants.length === 0}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Add dashboard
          </button>
        </div>
      </div>

      {dashboardsQ.isLoading && (
        <div className="text-gray-400">Loading…</div>
      )}

      {!dashboardsQ.isLoading && dashboards.length === 0 && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6 text-center text-gray-400">
          No tenant dashboard yet. Pick a tenant to push its first dashboard to
          Grafana.
        </div>
      )}

      <div className="space-y-3">
        {dashboards.map((d) => (
          <DashboardCard
            key={d.id}
            dashboard={d}
            tenants={tenants}
            onResync={() => syncMut.mutate(d.tenant_id)}
            onDelete={() => {
              if (confirm(`Drop dashboard for tenant ${d.label}?`)) {
                deleteMut.mutate(d.tenant_id);
              }
            }}
            isPending={syncMut.isPending || deleteMut.isPending}
          />
        ))}
      </div>

      {createOpen && (
        <CreateDashboardModal
          tenants={tenants}
          existingTenantIds={new Set(dashboards.map((d) => d.tenant_id))}
          onClose={() => setCreateOpen(false)}
          onSubmit={(payload) => createMut.mutate(payload)}
          pending={createMut.isPending}
          error={createMut.error ? String(createMut.error) : null}
        />
      )}
    </div>
  );
}

function DashboardCard({
  dashboard,
  tenants,
  onResync,
  onDelete,
  isPending,
}: {
  dashboard: MonitoringDashboard;
  tenants: Tenant[];
  onResync: () => void;
  onDelete: () => void;
  isPending: boolean;
}) {
  const tenant = tenants.find((t) => t.id === dashboard.tenant_id);
  const grafanaHref = dashboard.grafana_url
    ? `${window.location.protocol}//${window.location.hostname}:3000${dashboard.grafana_url}`
    : null;

  const inError = !!dashboard.last_error;
  const tone = inError
    ? "border-red-700/40"
    : dashboard.grafana_uid
      ? "border-emerald-700/30"
      : "border-amber-700/40";

  return (
    <div className={`rounded-lg border ${tone} bg-gray-900 p-4`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-100">{dashboard.label}</h3>
            {tenant && (
              <span className="rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">
                tenant {tenant.slug}
              </span>
            )}
            {dashboard.grafana_uid ? (
              <span className="flex items-center gap-1 rounded bg-emerald-600/20 px-1.5 py-0.5 text-xs text-emerald-300">
                <CheckCircle className="h-3 w-3" /> synced
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded bg-amber-600/20 px-1.5 py-0.5 text-xs text-amber-300">
                <AlertTriangle className="h-3 w-3" /> not in grafana
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            UID: <code>{dashboard.grafana_uid ?? "—"}</code> · Last sync:{" "}
            {dashboard.last_synced_at
              ? new Date(dashboard.last_synced_at).toLocaleString()
              : "never"}
          </p>
          {inError && (
            <p className="mt-2 rounded border border-red-800/40 bg-red-900/20 px-2 py-1 text-xs text-red-300">
              {dashboard.last_error}
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {grafanaHref && (
            <a
              href={grafanaHref}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700"
            >
              Open <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <button
            onClick={onResync}
            disabled={isPending}
            className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={"h-3 w-3 " + (isPending ? "animate-spin" : "")} />
            Re-sync
          </button>
          <button
            onClick={onDelete}
            disabled={isPending}
            aria-label="Delete dashboard"
            className="rounded border border-red-700/50 bg-red-700/10 px-2 py-1 text-xs text-red-300 hover:bg-red-700/20 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateDashboardModal({
  tenants,
  existingTenantIds,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  tenants: Tenant[];
  existingTenantIds: Set<number>;
  onClose: () => void;
  onSubmit: (p: { tenant_id: number; label?: string | null }) => void;
  pending: boolean;
  error: string | null;
}) {
  const eligible = useMemo(
    () => tenants.filter((t) => !existingTenantIds.has(t.id)),
    [tenants, existingTenantIds],
  );
  const [tenantId, setTenantId] = useState<number | null>(eligible[0]?.id ?? null);
  const [label, setLabel] = useState("");

  const canSubmit = tenantId !== null && eligible.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-gray-100">
          Add tenant dashboard
        </h3>

        {eligible.length === 0 ? (
          <p className="text-sm text-gray-400">
            Every existing tenant already has a dashboard. Delete one first if
            you want to recreate it.
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!canSubmit || tenantId === null) return;
              onSubmit({
                tenant_id: tenantId,
                label: label.trim() || null,
              });
            }}
            className="space-y-3"
          >
            <label className="block text-sm">
              <span className="text-gray-400">Tenant</span>
              <select
                value={tenantId ?? ""}
                onChange={(e) => setTenantId(Number(e.target.value))}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              >
                {eligible.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-400">
                Label <span className="text-gray-600">(optional)</span>
              </span>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Defaults to the tenant's name"
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              />
            </label>
            {error && <div className="text-sm text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || pending}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {pending ? "Pushing…" : "Add"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
